import type { HsCullingObject } from './spatial-culling.class';

/** Minimum projected size in CSS pixels below which objects are skipped while navigating; 0 disables it. */
export type HsNavigationMinScreenSize = 0 | 8 | 16 | 32;

interface NavigationCullingCamera {
  worldMatrix: { values: readonly number[] };
  projectionMatrix: { values: readonly number[] };
  near: number;
}

interface NavigationCullingDecision {
  revision: number;
  visible: boolean;
  x: number;
  y: number;
  z: number;
  radius: number;
}

const RESTORE_SIZE_MULTIPLIER: number = 1.15;

export class HsNavigationCulling<T extends HsCullingObject> {
  private readonly decisions: WeakMap<T, NavigationCullingDecision> = new WeakMap();
  private revision: number = 0;
  // Decisions older than this revision belong to an earlier navigation and carry no hysteresis.
  private firstValidRevision: number = 0;
  private lastRefreshAt: number = Number.NEGATIVE_INFINITY;
  private enabled: boolean = false;
  private threshold: HsNavigationMinScreenSize = 0;
  private focalX: number = 0;
  private focalY: number = 0;
  private near: number = 0;
  private matrix: readonly number[] = [];

  constructor(private readonly refreshIntervalMs: number) {}

  public invalidate(): void {
    // Retire decisions by revision instead of replacing the map: re-inserting every visible object into a new
    // WeakMap cost tens of milliseconds on the first culling pass of each navigation.
    this.revision++;
    this.firstValidRevision = this.revision;
    this.lastRefreshAt = Number.NEGATIVE_INFINITY;
  }

  public begin(
    camera: NavigationCullingCamera,
    canvasSize: { realWidth: number; realHeight: number } | null,
    threshold: HsNavigationMinScreenSize,
    navigating: boolean,
    now: number
  ): void {
    if (!navigating || threshold === 0 || canvasSize === null) {
      if (this.enabled) {
        this.invalidate();
      }
      this.enabled = false;
      return;
    }
    const focalX: number = Math.abs(camera.projectionMatrix.values[0]) * canvasSize.realWidth;
    const focalY: number = Math.abs(camera.projectionMatrix.values[5]) * canvasSize.realHeight;
    if (!this.enabled || threshold !== this.threshold) {
      this.invalidate();
    }
    if (
      focalX !== this.focalX ||
      focalY !== this.focalY ||
      camera.near !== this.near ||
      now - this.lastRefreshAt >= this.refreshIntervalMs
    ) {
      this.revision++;
      this.lastRefreshAt = now;
    }
    this.enabled = true;
    this.threshold = threshold;
    this.focalX = focalX;
    this.focalY = focalY;
    this.near = camera.near;
    this.matrix = camera.worldMatrix.values;
  }

  public isVisible(object: T): boolean {
    if (!this.enabled) {
      return true;
    }
    const { center, radius } = object.boundingSphere;
    const stored: NavigationCullingDecision | undefined = this.decisions.get(object);
    const decision: NavigationCullingDecision | undefined =
      stored !== undefined && stored.revision >= this.firstValidRevision ? stored : undefined;
    if (
      decision !== undefined &&
      decision.revision === this.revision &&
      decision.x === center.x &&
      decision.y === center.y &&
      decision.z === center.z &&
      decision.radius === radius
    ) {
      return decision.visible;
    }
    const threshold: number = this.threshold * (decision?.visible === false ? RESTORE_SIZE_MULTIPLIER : 1);
    const visible: boolean = this.isSphereLargeEnough(center, radius, threshold);
    if (stored === undefined) {
      this.decisions.set(object, {
        revision: this.revision,
        visible,
        x: center.x,
        y: center.y,
        z: center.z,
        radius,
      });
    } else {
      stored.revision = this.revision;
      stored.visible = visible;
      stored.x = center.x;
      stored.y = center.y;
      stored.z = center.z;
      stored.radius = radius;
    }
    return visible;
  }

  private isSphereLargeEnough(center: { x: number; y: number; z: number }, radius: number, threshold: number): boolean {
    const dx: number = center.x - this.matrix[12];
    const dy: number = center.y - this.matrix[13];
    const dz: number = center.z - this.matrix[14];
    const depth: number = -(dx * this.matrix[8] + dy * this.matrix[9] + dz * this.matrix[10]);
    if (!(radius > 0) || !Number.isFinite(radius) || !Number.isFinite(depth) || depth <= radius + this.near) {
      return true;
    }
    const x: number = dx * this.matrix[0] + dy * this.matrix[1] + dz * this.matrix[2];
    const y: number = dx * this.matrix[4] + dy * this.matrix[5] + dz * this.matrix[6];
    // Conservative projected diameter, including off-axis growth, without division or trig.
    const limit: number = threshold * depth * (depth - radius);
    return (
      this.focalX * radius * (depth + Math.abs(x)) >= limit || this.focalY * radius * (depth + Math.abs(y)) >= limit
    );
  }
}
